import { useAtom } from "@effect/atom-react"
import { applyTheme, oppositeTheme, themeAtom } from "@ffn/app-kit"
import { Container, Mono, ThemeToggle, Topbar } from "@ffn/components"
import { createRootRoute, HeadContent, Link, Outlet, useHydrated } from "@tanstack/react-router"
import { useEffect } from "react"
import { DESCRIPTIONS, SITE } from "../seo/index.js"

/**
 * The root carries the fallback head every page inherits (a child route's own
 * `head()` wins tag by tag), and `<HeadContent />` is what turns each route's
 * head() into real `<title>`, `<meta>`, `<link>` and JSON-LD tags. React 19
 * hoists the first three into `<head>`; the JSON-LD script stays where it is
 * rendered, which is fine for every consumer that reads it.
 */
export const Route = createRootRoute({
  head: () => ({
    meta: [
      { title: SITE.name },
      { name: "description", content: DESCRIPTIONS.home },
      { name: "robots", content: "index,follow" }
    ]
  }),
  component: RootLayout
})

const NAV = [
  { to: "/", label: "Home" },
  { to: "/notes", label: "Frontier Field Notes" },
  { to: "/about", label: "About" }
] as const

/** The year the site was built or rebuilt; a prerendered footer never claims a later one. */
const YEAR = new Date().getFullYear()

function ThemeControl() {
  const [theme, setTheme] = useAtom(themeAtom)
  // The stored preference is only knowable in a browser. Until the page has
  // hydrated the control shows the light-theme glyph, which is what the
  // prerendered HTML holds, so the first client render matches it.
  const hydrated = useHydrated()
  useEffect(() => {
    applyTheme(theme)
  }, [theme])
  return (
    <ThemeToggle
      theme={hydrated ? theme : "light"}
      onToggle={() => setTheme(oppositeTheme(theme))}
    />
  )
}

function RootLayout() {
  return (
    <div className="SiteFrame">
      <HeadContent />
      <a className="SiteSkipLink" href="#main-content">
        Skip to content
      </a>
      <Topbar
        width="wide"
        brand={
          <Link className="SiteBrand" to="/">
            Laith Al-Saadoon
          </Link>
        }
        actions={<ThemeControl />}
      >
        <nav aria-label="Primary" className="SiteNav">
          {NAV.map((item) => (
            <Link
              activeOptions={{ exact: item.to === "/" }}
              activeProps={{ "aria-current": "page" }}
              className="SiteNavLink"
              key={item.to}
              to={item.to}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      </Topbar>
      <main className="SiteMain" id="main-content" tabIndex={-1}>
        <Container width="wide">
          <Outlet />
        </Container>
      </main>
      <footer className="SiteFooter">
        <Container width="wide">
          <Mono>
            <span suppressHydrationWarning>© {YEAR} Laith Al-Saadoon</span>
          </Mono>
          <nav aria-label="Footer" className="SiteFooterLinks">
            <a href="/feed.xml">RSS</a>
            <Link to="/notes">Field notes</Link>
            <a href="https://www.linkedin.com/in/laithalsaadoon">Contact</a>
          </nav>
        </Container>
      </footer>
    </div>
  )
}
